import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { Metrics } from 'components/insights/constants';
import { formatPercentage } from 'components/insights/formatting';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { useInboundReferralsInsightsStore } from 'stores/inboundReferralsInsightsStore';
import { colors } from 'styles/theme/colors';

import MetricDetailLayout from '../../shared/slideout/MetricDetailLayout';
import { StatTile } from '../../shared/slideout/statTiles';
import { INBOUND_DIMENSION_LABELS } from '../constants';

import { buildOverlaidColumnChart, OVERLAID_COLUMN_CHART_HEIGHT } from './overlaidColumnChart';
import useInboundSelectedFacilityExtremes from './useInboundSelectedFacilityExtremes';

type Props = {
  loading: boolean;
  initialLoading: boolean;
  rollups: AnalyticsResponseRollupCollection;
  data: AnalyticsResponseData;
};

// Metric drill for the Selected rate tile: selection rate + top selected SNF, over an overlaid
// "closed vs selected" bar per View-By entity. Selection is gate-free (a building can be selected
// regardless of its own determination); the rate's denominator is buildings with a recorded outcome.
export default function SelectedDetailHeader({ loading, initialLoading, rollups, data }: Props) {
  const slideoutDimension = useInboundReferralsInsightsStore((state) => state.slideoutDimension);

  const overall = rollups.overall.metrics;
  const selectedRate = overall.getCurrentDataPoint(Metrics.SELECTED_RATE, 0);
  const selectedCount = overall.getCurrentDataPoint(Metrics.SELECTED_COUNT, 0) ?? 0;
  // "Closed" = every building that reached a recorded outcome (selected + not_selected), read from
  // the closed_count metric.
  const referralsClosed = overall.getCurrentDataPoint(Metrics.CLOSED_COUNT, 0) ?? 0;

  const { most: mostFacility, loading: mostFacilityLoading } = useInboundSelectedFacilityExtremes();

  const entityLabel = slideoutDimension ? (INBOUND_DIMENSION_LABELS[slideoutDimension] ?? '') : '';

  // Sort by selected rate desc — matches the breakdown table's default sort. The grey base is
  // "Total Closed" (buildings with a recorded outcome); selection is gate-free so closed (not
  // accepted) is the correct base, and selected ⊆ closed.
  const { chartOptions, scrollConfig, isEmpty } = useMemo(
    () =>
      buildOverlaidColumnChart({
        names: data.dimensions.getCurrentDimensionValuesByIndex(0),
        rates: data.metrics.getCurrentSeries(Metrics.SELECTED_RATE),
        baseSeries: { name: 'Total Closed', values: data.metrics.getCurrentSeries(Metrics.CLOSED_COUNT) },
        overlaySeries: {
          name: 'Total Selected',
          values: data.metrics.getCurrentSeries(Metrics.SELECTED_COUNT),
          color: colors.blue100,
        },
      }),
    [data]
  );

  return (
    <MetricDetailLayout
      tiles={
        <>
          <StatTile
            label='Avg. Selected Rate'
            loading={initialLoading}
            inlineAddendum
            primary={formatPercentage(selectedRate)}
            addendum={selectedRate != null ? `${selectedCount} of ${referralsClosed} total closed` : undefined}
          />
          <StatTile
            label='Top Selected SNF'
            tooltipText='Based on Selected Rate'
            loading={mostFacilityLoading}
            primary={mostFacility?.name ?? '\u2014'}
          />
        </>
      }
      columns={2}
      chartTitle={`Selected Rate by ${entityLabel}`}
      chartLoading={loading}
      chartHeight={OVERLAID_COLUMN_CHART_HEIGHT}
      isEmpty={isEmpty}
      scrollConfig={scrollConfig}
      emptyMessage='No selected referrals to show for this time period.'>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </MetricDetailLayout>
  );
}
